import { Form, FormMethod, useNavigation } from "@remix-run/react";
import React, { FormEvent, PropsWithChildren, useContext, useEffect, useRef } from "react";
import {
  FormFieldsActionFunction,
  FormFieldsLoaderFunction,
  useFields,
} from "../hooks/use-fields.ts";
import { Fieldset, FieldsManager, FormContext } from "../lib/form-fields.ts";
import { GenericErrors } from "./form-fields.tsx";
import { useResult } from "../hooks/use-result.ts";

export function ValidatedFormFor<T extends Fieldset>() {
  function ValidatedForm(
    props:
      & PropsWithChildren
      & React.HTMLAttributes<HTMLFormElement>
      & {
        method?: FormMethod;
        beforeSubmit?: (manager: FieldsManager<T>) => Promise<void | string[]>;
        manager: FieldsManager<T>;
        // onEffect?: (manager: FieldsManager<T>) => void | Promise<void>;
        // effectedBy?: any[];
        action?: string;
      },
  ) {
    const formRef = useRef<HTMLFormElement>(null);
    // const manager = useFields<T>();
    const { manager } = props;
    const navigation = useNavigation();

    const isSubmitting = navigation.state === "submitting" || manager.tryingSubmit;

    const validateFields = function (event: FormEvent<HTMLFormElement>) {
      event.preventDefault();
      manager.trySubmit({ beforeHook: props.beforeSubmit });
    };

    /*
  this makes browser autofill work with FieldsManager
  */
    useEffect(() => {
      setTimeout(() => {
        const inputs: NodeListOf<HTMLInputElement> = document.querySelectorAll(
          "input.FormField__Input",
        );
        inputs.forEach((input) => {
          if (input.matches(":autofill")) {
            manager.setValue(input.name, input.value);
          }
        });
      }, 1000);
    }, []);

    return (
      <FormContext.Provider value={manager}>
        <Form
          ref={formRef}
          method={props.method || "post"}
          onSubmit={(event: FormEvent<HTMLFormElement>) => {
            validateFields(event);
          }}
          className={`ValidatedForm ${props.className ? props.className : ""} ${
            isSubmitting ? "--submitting" : ""
          }`}
          action={props.action ? props.action : undefined}
        >
          <GenericErrors />
          <fieldset disabled={isSubmitting}>
            {props.children}
          </fieldset>
        </Form>
      </FormContext.Provider>
    );
  }
}

export function ValidatedForm<
  T extends Fieldset,
>(
  props:
    & PropsWithChildren
    & React.HTMLAttributes<HTMLFormElement>
    & {
      method?: FormMethod;
      beforeSubmit?: (manager: FieldsManager<T>) => Promise<void | string[]>;
      manager: FieldsManager<T>;
      // onEffect?: (manager: FieldsManager<T>) => void | Promise<void>;
      // effectedBy?: any[];
      action?: string;
    },
) {
  const formRef = useRef<HTMLFormElement>(null);
  // const manager = useFields<T>();
  const { manager } = props;
  const navigation = useNavigation();

  const isSubmitting = navigation.state === "submitting" || manager.tryingSubmit;

  const validateFields = function (event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    manager.trySubmit({ beforeHook: props.beforeSubmit });
  };

  /*
  this makes browser autofill work with FieldsManager
  */
  useEffect(() => {
    setTimeout(() => {
      const inputs: NodeListOf<HTMLInputElement> = document.querySelectorAll(
        "input.FormField__Input",
      );
      inputs.forEach((input) => {
        if (input.matches(":autofill")) {
          manager.setValue(input.name, input.value);
        }
      });
    }, 1000);
  }, []);

  return (
    <FormContext.Provider value={manager}>
      <Form
        ref={formRef}
        method={props.method || "post"}
        onSubmit={(event: FormEvent<HTMLFormElement>) => {
          event.preventDefault();
          validateFields(event);
        }}
        className={`ValidatedForm ${props.className ? props.className : ""} ${
          isSubmitting ? "--submitting" : ""
        }`}
        action={props.action ? props.action : undefined}
      >
        <GenericErrors />
        <fieldset disabled={isSubmitting}>
          {props.children}
        </fieldset>
      </Form>
    </FormContext.Provider>
  );
}

ValidatedForm.Success = (props: React.PropsWithChildren) => {
  const manager = useContext(FormContext)!;
  const { confirmation, errors } = useResult();
  const noErrors = !(errors && errors.length > 0);

  return manager.isSuccessful && noErrors && (
    <div className="ValidatedForm__SuccessOrFailure --success">
      {props.children}
    </div>
  );
};

ValidatedForm.Failure = (props: React.PropsWithChildren) => {
  const manager = useContext(FormContext)!;

  return manager.isFailed && (
    <div className="ValidatedForm__SuccessOrFailure --failure">
      {props.children}
    </div>
  );
};
